import { BRAND_NAME, CONTACT_EMAIL, RENAME_DATE, type Locale } from "@kmstories/shared";

/**
 * Privacy policy + submission terms, structured so each section can be
 * translated and reviewed independently. The Persian text must be a full legal
 * equivalent — it is drafted from the English and MUST be verified by a native
 * speaker before launch (see docs/LAUNCH-CHECKLIST.md).
 *
 * CONTROLLER: Sami's decision (2026-09-02) is to name the project itself,
 * reachable at the alias. GDPR Art 13 ultimately expects a natural or legal
 * person behind that name; swap in the entity (e.g. Covey AB) here when decided.
 */
export const CONTROLLER = `${BRAND_NAME} (the documentary's production team), reachable at ${CONTACT_EMAIL}`;
/** Re-exported so the legal pages keep one import; the value lives in @kmstories/shared brand.ts. */
export { CONTACT_EMAIL };
/**
 * The rename to Qissakhana is the current version of these texts. The previous
 * version (effective 2026-09-01, under the name KM Stories) is in git history.
 */
export const EFFECTIVE_DATE = RENAME_DATE;

/** The one-line rename notice. Contributors who gave permissions to KM Stories should not have to infer this. */
const RENAMED_EN = `${BRAND_NAME} was called KM Stories until ${RENAME_DATE}; permissions given to KM Stories carry over to ${BRAND_NAME}, which is the same team.`;
const RENAMED_FA = `قصه\u200cخانه تا تاریخ \u2066${RENAME_DATE}\u2069 «کی‌ام استوریز» نام داشت؛ اجازه‌هایی که به «کی‌ام استوریز» داده شده به قصه\u200cخانه منتقل می‌شود، که همان تیم است.`;

export interface LegalSection {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
}

export interface LegalDoc {
  title: string;
  intro: string;
  sections: LegalSection[];
  /** Shown when a locale's text has not yet been reviewed by a native speaker. */
  reviewNotice?: string;
}

const privacyEn: LegalDoc = {
  title: "Privacy policy",
  intro: `This policy explains what happens to the personal data you give Qissakhana through this website, why we process it, and the rights you have. It applies from ${EFFECTIVE_DATE}. We wrote it to be read, not skimmed; if anything is unclear, write to ${CONTACT_EMAIL}.`,
  sections: [
    {
      heading: "Who is responsible",
      paragraphs: [
        `The data controller is ${CONTROLLER}, the producer of the Qissakhana documentaries. You can reach us at ${CONTACT_EMAIL}. There is no separate data protection officer; that address reaches the people who handle your data.`,
        RENAMED_EN,
      ],
    },
    {
      heading: "What we collect, and why",
      paragraphs: ["We collect only what each form asks for. Nothing on this site tracks you across other sites, and no advertising or analytics scripts run here."],
      bullets: [
        "Story submissions: your story (text, voice note, video or a link), your chosen way of being credited (real name, pseudonym or anonymous), and an optional email address. Purpose: to consider your story for the documentary and related productions, and to reach you if you asked us to. Lawful basis: your consent (GDPR Art 6(1)(a)) and, because a personal story can reveal political opinions, religious beliefs, ethnic origin or health, your explicit consent to process such special-category data (Art 9(2)(a)). You can submit with no name and no email.",
        "Support interest: your email, optionally your name, how you would like to help, and a message. Purpose: to tell you when supporting the film becomes possible. Lawful basis: consent.",
        "Contact and casting: your name, email, message and, for casting, optionally a phone number, languages, location and links. Purpose: to answer you and, if you asked, to keep your details for future casting calls. Lawful basis: consent, and our legitimate interest in answering messages sent to us.",
        "Updates list: your email and language. Purpose: occasional news about the film. Lawful basis: consent, confirmed by a second click (double opt-in).",
        "Technical data: this site does not store IP addresses with anything you submit and does not set tracking cookies. The admin area uses one login cookie. Our hosting provider's server logs may record IP addresses for security for a short period.",
      ],
    },
    {
      heading: "Stories are private by default",
      paragraphs: [
        "A submitted story goes only to the small Qissakhana team and is stored on our own server in Sweden. It is never published or shown publicly unless you have separately and explicitly given permission (the film licence or the web-excerpt permission on the form), and even then we contact you first if you gave us a way to.",
        "Location and device metadata is removed from every photo, video and voice note the moment it arrives. Notification emails to the team contain no story content.",
      ],
    },
    {
      heading: "People mentioned in your story",
      paragraphs: [
        "Stories often mention other people: family, friends, officials. We hold that information only as part of your story, review it privately, and remove details that could identify third parties before anything is ever published. If you are worried about someone named in your story, tell us and we will treat that part with extra care.",
      ],
    },
    {
      heading: "Who else sees your data",
      paragraphs: ["We use a small number of service providers, each bound by a data processing agreement:"],
      bullets: [
        "Hosting: our own server at GleSYS AB in Sweden (EU). Your data does not leave the EU for storage.",
        "Email delivery: Postmark (ActiveCampaign, LLC, USA) sends confirmation and notification emails. Only email addresses and the content of those emails pass through Postmark; stories never do. Transfers to the USA rely on the EU-US Data Privacy Framework and standard contractual clauses.",
        "YouTube (Google): only if you gave the film licence or web-excerpt permission and material is later published, it appears on YouTube like any part of the film. Watching clips on this site loads nothing from Google until you press play.",
        "No one else. We do not sell, rent or share your data with anyone for their own purposes.",
      ],
    },
    {
      heading: "How long we keep things",
      paragraphs: ["Retention is enforced by the software, not just promised:"],
      bullets: [
        "Stories: until you withdraw them, or until the project ends and a further 24 months have passed for stories that were not used.",
        "Consent records for material used in a production: for the life of the production, because distributors and insurers require proof of consent (chain of title). This survives withdrawal; new uses stop, but the record stays.",
        "Uploaded files never attached to a finished submission: deleted after 24 hours.",
        "Support and contact messages: 24 months after the last activity, then deleted or scrubbed. Casting details you asked us to keep on file: deleted automatically after 24 months of inactivity, or on request.",
        "Unconfirmed updates-list signups: deleted after 30 days. Confirmed subscribers: until you unsubscribe.",
        "Records of deletions: we keep a log of what was deleted and when, never of who asked.",
      ],
    },
    {
      heading: "Your rights",
      paragraphs: [
        `You can ask for access to your data, correction, erasure, restriction, portability, and you can object to processing based on legitimate interest. You can withdraw any consent at any time; withdrawal does not affect what happened before, and material already published or distributed cannot be recalled. To exercise a right, use your private withdrawal link or write to ${CONTACT_EMAIL}. We answer within one month.`,
        "If you think we have handled your data wrongly, you can complain to the Swedish Authority for Privacy Protection (Integritetsskyddsmyndigheten, IMY, imy.se) or to the supervisory authority where you live.",
      ],
    },
    {
      heading: "Your withdrawal link",
      paragraphs: [
        "Every submission comes with a private link that lets you withdraw it or change how you are credited, without an account and without an email address. If you gave us an email, we send the link there too. If you lose the link and gave us no email, we have no way to verify that a story is yours, and cannot withdraw it for you; please keep the link safe.",
      ],
    },
    {
      heading: "Children",
      paragraphs: ["This site is for adults. We do not knowingly accept stories from anyone under 18 through the form. If you are under 18, write to us and we will explain how a parent or guardian can take part with you."],
    },
    {
      heading: "Security",
      paragraphs: ["Everything travels over HTTPS. Data lives on an encrypted server in Sweden with access limited to the Qissakhana team. Uploaded media is checked, stripped of metadata and stored under random names. Admin access requires two independent credentials. No system is perfect; if a breach ever affects you, we will tell you and the authority within the legal deadlines."],
    },
    {
      heading: "No automated decisions",
      paragraphs: ["No decision about you or your story is made automatically. Every story is read or listened to by a person."],
    },
    {
      heading: "Changes",
      paragraphs: ["If this policy changes materially, we update the date above and, where we can, tell people on the updates list. The consent text you agreed to when submitting is kept unchanged as the version you saw."],
    },
  ],
};

const termsEn: LegalDoc = {
  title: "Submission terms",
  intro: `Short, plain terms for sharing a story with Qissakhana. They sit beside the consent boxes on the form, which say exactly what you allow us to do. ${RENAMED_EN}`,
  sections: [
    {
      heading: "What you may submit",
      paragraphs: ["Your own story, in your own words, voice or footage, or material you have the right to share. You must be 18 or older."],
      bullets: [
        "Do not submit content you do not have the right to share, or that reveals private details about someone else in a way that could harm them.",
        "Do not submit anything unlawful, threatening or intended to deceive.",
        "Links you paste point to your own public accounts and are outside our protections.",
      ],
    },
    {
      heading: "What we do with it",
      paragraphs: [
        "We store and review your submission privately. We only use it in the film, in future productions, or on our website and social channels if you ticked the matching permission on the form, and we contact you first if we can. You keep the copyright to your story.",
        "We may decline or remove any submission at our discretion, and we must report content that the law requires us to report.",
      ],
    },
    {
      heading: "Withdrawing",
      paragraphs: ["Use your private link or email us. Unused material is deleted. Material already used in a production keeps its consent record; new uses stop, published copies cannot be recalled."],
    },
    {
      heading: "Liability and law",
      paragraphs: ["The site is provided as is. We are a small team and cannot promise to reply to every story. Swedish law applies, and Swedish courts have jurisdiction, without limiting the consumer rights you have where you live."],
    },
  ],
};

/** Persian drafts: translated from the English; PENDING native-speaker review. */
const privacyFa: LegalDoc = {
  title: "سیاست حریم خصوصی",
  intro: `این سند توضیح می‌دهد با داده‌های شخصی‌ای که از طریق این وب‌سایت به قصه\u200cخانه می‌دهید چه می‌کنیم، چرا آن‌ها را پردازش می‌کنیم و چه حقوقی دارید. این سیاست از تاریخ ${EFFECTIVE_DATE} اعمال می‌شود. آن را برای خوانده‌شدن نوشته‌ایم، نه برای ورق‌زدن؛ اگر چیزی روشن نیست، به ${CONTACT_EMAIL} بنویسید.`,
  reviewNotice: "این ترجمه از متن انگلیسی تهیه شده و در انتظار بازبینی یک فارسی‌زبان است. در صورت اختلاف، متن انگلیسی معتبر است.",
  sections: [
    {
      heading: "مسئول داده‌ها کیست",
      paragraphs: [`کنترل‌کنندهٔ داده ${CONTROLLER} است، تهیه‌کنندهٔ مستندهای قصه\u200cخانه. می‌توانید از طریق ${CONTACT_EMAIL} با ما در تماس باشید. مسئول جداگانه‌ای برای حفاظت از داده وجود ندارد؛ این نشانی به همان افرادی می‌رسد که با داده‌های شما سروکار دارند.`, RENAMED_FA],
    },
    {
      heading: "چه چیزهایی جمع می‌کنیم و چرا",
      paragraphs: ["فقط آن‌چه هر فرم می‌خواهد را جمع می‌کنیم. هیچ‌چیز در این سایت شما را در سایت‌های دیگر دنبال نمی‌کند و هیچ اسکریپت تبلیغاتی یا تحلیلی این‌جا اجرا نمی‌شود."],
      bullets: [
        "ارسال داستان: داستان شما (متن، پیام صوتی، ویدئو یا لینک)، شیوهٔ انتخابی‌تان برای ذکر نام (نام واقعی، نام مستعار یا بی‌نام) و یک نشانی ایمیل اختیاری. هدف: بررسی داستان شما برای مستند و تولیدات مرتبط، و تماس با شما اگر خودتان خواسته باشید. مبنای قانونی: رضایت شما (مادهٔ ۶(۱)(الف) GDPR) و، چون یک داستان شخصی می‌تواند دیدگاه‌های سیاسی، باورهای مذهبی، قومیت یا وضعیت سلامت را آشکار کند، رضایت صریح شما برای پردازش این داده‌های ویژه (مادهٔ ۹(۲)(الف)). می‌توانید بدون نام و بدون ایمیل ارسال کنید.",
        "علاقه به حمایت: ایمیل شما، در صورت تمایل نامتان، شیوهٔ کمکی که می‌خواهید و یک پیام. هدف: خبر دادن به شما وقتی حمایت از فیلم ممکن شود. مبنای قانونی: رضایت.",
        "تماس و بازیگری: نام، ایمیل، پیام شما و، برای بازیگری، به‌صورت اختیاری شمارهٔ تلفن، زبان‌ها، محل زندگی و لینک‌ها. هدف: پاسخ به شما و، اگر خواسته باشید، نگهداری مشخصاتتان برای فراخوان‌های آینده. مبنای قانونی: رضایت، و منفعت مشروع ما در پاسخ‌دادن به پیام‌هایی که برایمان فرستاده می‌شود.",
        "فهرست خبرها: ایمیل و زبان شما. هدف: خبرهای گاه‌به‌گاه فیلم. مبنای قانونی: رضایت، تأییدشده با کلیک دوم (تأیید دومرحله‌ای).",
        "داده‌های فنی: این سایت نشانی IP را همراه هیچ‌چیزی که ارسال می‌کنید ذخیره نمی‌کند و کوکی ردیابی نمی‌گذارد. بخش مدیریت از یک کوکی ورود استفاده می‌کند. لاگ‌های سرور ارائه‌دهندهٔ میزبانی ما ممکن است برای امنیت، نشانی‌های IP را برای مدت کوتاهی نگه دارند.",
      ],
    },
    {
      heading: "داستان‌ها به‌طور پیش‌فرض خصوصی‌اند",
      paragraphs: [
        "داستان ارسال‌شده فقط به تیم کوچک قصه\u200cخانه می‌رسد و روی سرور خودمان در سوئد نگهداری می‌شود. هرگز منتشر یا به‌صورت عمومی نمایش داده نمی‌شود مگر آن‌که شما جداگانه و صریحاً اجازه داده باشید (مجوز فیلم یا اجازهٔ انتشار بخش‌ها در وب، در فرم)، و حتی در آن صورت، اگر راهی برای تماس داده باشید، اول با شما تماس می‌گیریم.",
        "اطلاعات مکان و دستگاه از هر عکس، ویدئو و پیام صوتی همان لحظهٔ رسیدن پاک می‌شود. ایمیل‌های اطلاع‌رسانی به تیم هیچ محتوایی از داستان ندارند.",
      ],
    },
    {
      heading: "افرادی که در داستان شما نام برده می‌شوند",
      paragraphs: ["داستان‌ها اغلب از دیگران هم می‌گویند: خانواده، دوستان، مسئولان. ما آن اطلاعات را فقط به‌عنوان بخشی از داستان شما نگه می‌داریم، به‌صورت خصوصی بررسی می‌کنیم و پیش از هر انتشاری، جزئیاتی که می‌تواند اشخاص ثالث را شناسایی کند حذف می‌کنیم. اگر دربارهٔ کسی که در داستانتان نام برده‌اید نگرانید، به ما بگویید تا با آن بخش با دقت بیشتری رفتار کنیم."],
    },
    {
      heading: "چه کس دیگری داده‌های شما را می‌بیند",
      paragraphs: ["از تعداد کمی ارائه‌دهندهٔ خدمات استفاده می‌کنیم که هر یک به قرارداد پردازش داده متعهدند:"],
      bullets: [
        "میزبانی: سرور خودمان در شرکت GleSYS AB در سوئد (اتحادیهٔ اروپا). داده‌های شما برای نگهداری از اتحادیهٔ اروپا خارج نمی‌شود.",
        "ارسال ایمیل: Postmark (شرکت ActiveCampaign, LLC، ایالات متحده) ایمیل‌های تأیید و اطلاع‌رسانی را می‌فرستد. فقط نشانی‌های ایمیل و محتوای همان ایمیل‌ها از Postmark می‌گذرد؛ داستان‌ها هرگز. انتقال به ایالات متحده بر پایهٔ چارچوب حریم خصوصی داده‌های اتحادیهٔ اروپا–ایالات متحده و بندهای قراردادی استاندارد انجام می‌شود.",
        "یوتیوب (گوگل): فقط اگر مجوز فیلم یا اجازهٔ انتشار بخش‌ها را داده باشید و مطلبی بعداً منتشر شود، مثل هر بخش دیگر فیلم در یوتیوب قرار می‌گیرد. تماشای کلیپ‌ها در این سایت تا وقتی دکمهٔ پخش را نزنید هیچ‌چیزی از گوگل بارگذاری نمی‌کند.",
        "هیچ‌کس دیگر. ما داده‌های شما را به هیچ‌کس برای اهداف خودش نمی‌فروشیم، اجاره نمی‌دهیم و به اشتراک نمی‌گذاریم.",
      ],
    },
    {
      heading: "چه مدت نگه می‌داریم",
      paragraphs: ["نگهداری داده توسط نرم‌افزار اجرا می‌شود، نه فقط وعده داده می‌شود:"],
      bullets: [
        "داستان‌ها: تا زمانی که آن‌ها را پس بگیرید، یا تا پایان پروژه و گذشت ۲۴ ماه دیگر برای داستان‌هایی که استفاده نشده‌اند.",
        "سوابق رضایت برای مطالب استفاده‌شده در یک تولید: برای تمام عمر آن تولید، چون توزیع‌کنندگان و بیمه‌گران مدرک رضایت (زنجیرهٔ مالکیت) را می‌خواهند. این سابقه پس از پس‌گرفتن هم می‌ماند؛ استفاده‌های تازه متوقف می‌شود اما سابقه باقی است.",
        "فایل‌های آپلودشده‌ای که هرگز به یک ارسال کامل متصل نشده‌اند: پس از ۲۴ ساعت حذف می‌شوند.",
        "پیام‌های حمایت و تماس: ۲۴ ماه پس از آخرین فعالیت، سپس حذف یا پاک‌سازی. مشخصات بازیگری که خواسته‌اید نگه داریم: پس از ۲۴ ماه بی‌فعالیتی به‌طور خودکار، یا با درخواست، حذف می‌شود.",
        "ثبت‌نام‌های تأییدنشدهٔ فهرست خبرها: پس از ۳۰ روز حذف می‌شوند. مشترکان تأییدشده: تا زمان لغو اشتراک.",
        "سوابق حذف: ثبت می‌کنیم چه چیزی و کِی حذف شده، هرگز این‌که کی درخواست کرده.",
      ],
    },
    {
      heading: "حقوق شما",
      paragraphs: [
        `می‌توانید درخواست دسترسی به داده‌هایتان، تصحیح، حذف، محدودسازی و انتقال‌پذیری بدهید و به پردازش بر پایهٔ منفعت مشروع اعتراض کنید. می‌توانید هر رضایتی را در هر زمان پس بگیرید؛ پس‌گرفتن بر آن‌چه پیش‌تر انجام شده اثری ندارد و مطالبی که قبلاً منتشر یا توزیع شده‌اند قابل بازگرداندن نیستند. برای استفاده از حقوقتان، از پیوند خصوصیِ پس‌گرفتن استفاده کنید یا به ${CONTACT_EMAIL} بنویسید. ظرف یک ماه پاسخ می‌دهیم.`,
        "اگر فکر می‌کنید با داده‌هایتان نادرست رفتار کرده‌ایم، می‌توانید به نهاد حفاظت از حریم خصوصی سوئد (Integritetsskyddsmyndigheten، IMY، imy.se) یا به نهاد ناظر کشور محل زندگی‌تان شکایت کنید.",
      ],
    },
    {
      heading: "پیوند پس‌گرفتن شما",
      paragraphs: ["هر ارسال یک پیوند خصوصی دارد که با آن می‌توانید بدون حساب کاربری و بدون ایمیل، داستانتان را پس بگیرید یا نحوهٔ ذکر نامتان را تغییر دهید. اگر ایمیلی به ما داده باشید، پیوند را به آن‌جا هم می‌فرستیم. اگر پیوند را گم کنید و ایمیلی نداده باشید، راهی برای تأیید این‌که داستان از شماست نداریم و نمی‌توانیم آن را برایتان پس بگیریم؛ لطفاً پیوند را امن نگه دارید."],
    },
    {
      heading: "کودکان",
      paragraphs: ["این سایت برای بزرگسالان است. ما آگاهانه از طریق فرم، داستانی از افراد زیر ۱۸ سال نمی‌پذیریم. اگر زیر ۱۸ سال هستید، برایمان بنویسید تا توضیح دهیم چگونه یکی از والدین یا سرپرست شما می‌تواند همراه شما شرکت کند."],
    },
    {
      heading: "امنیت",
      paragraphs: ["همه‌چیز از طریق HTTPS منتقل می‌شود. داده‌ها روی سروری رمزگذاری‌شده در سوئد نگه داشته می‌شوند و دسترسی به آن محدود به تیم قصه\u200cخانه است. فایل‌های آپلودشده بررسی می‌شوند، اطلاعات جانبی‌شان پاک می‌شود و با نام‌های تصادفی نگهداری می‌شوند. دسترسی مدیریتی به دو اعتبارنامهٔ مستقل نیاز دارد. هیچ سیستمی بی‌نقص نیست؛ اگر نقض امنیتی‌ای روی شما اثر بگذارد، در مهلت‌های قانونی به شما و به نهاد ناظر خبر می‌دهیم."],
    },
    {
      heading: "بدون تصمیم‌گیری خودکار",
      paragraphs: ["هیچ تصمیمی دربارهٔ شما یا داستانتان به‌صورت خودکار گرفته نمی‌شود. هر داستان را یک انسان می‌خواند یا می‌شنود."],
    },
    {
      heading: "تغییرات",
      paragraphs: ["اگر این سیاست تغییر اساسی کند، تاریخ بالا را به‌روز می‌کنیم و، هر جا بتوانیم، به اعضای فهرست خبرها اطلاع می‌دهیم. متن رضایتی که هنگام ارسال با آن موافقت کرده‌اید، بدون تغییر و به‌عنوان همان نسخه‌ای که دیده‌اید نگه داشته می‌شود."],
    },
  ],
};

const termsFa: LegalDoc = {
  title: "شرایط ارسال",
  intro: `شرایط کوتاه و روشن برای در میان گذاشتن داستان با قصه\u200cخانه. این‌ها کنار گزینه‌های رضایت در فرم قرار می‌گیرند که دقیقاً می‌گویند چه اجازه‌ای به ما می‌دهید. ${RENAMED_FA}`,
  reviewNotice: "این ترجمه از متن انگلیسی تهیه شده و در انتظار بازبینی یک فارسی‌زبان است. در صورت اختلاف، متن انگلیسی معتبر است.",
  sections: [
    {
      heading: "چه چیزی می‌توانید بفرستید",
      paragraphs: ["داستان خودتان، با کلمات، صدا یا تصویر خودتان، یا مطلبی که حق به‌اشتراک‌گذاری آن را دارید. باید ۱۸ سال یا بیشتر داشته باشید."],
      bullets: [
        "مطلبی را که حق به‌اشتراک‌گذاری آن را ندارید، یا جزئیات خصوصی کس دیگری را به شکلی که بتواند به او آسیب بزند آشکار می‌کند، نفرستید.",
        "هیچ‌چیز غیرقانونی، تهدیدآمیز یا فریب‌دهنده نفرستید.",
        "لینک‌هایی که می‌گذارید به حساب‌های عمومی خودتان اشاره دارند و خارج از حفاظت‌های ما هستند.",
      ],
    },
    {
      heading: "ما با آن چه می‌کنیم",
      paragraphs: [
        "ارسال شما را به‌صورت خصوصی نگه می‌داریم و بررسی می‌کنیم. فقط اگر اجازهٔ مربوط را در فرم تیک زده باشید از آن در فیلم، تولیدات آینده، یا وب‌سایت و شبکه‌های اجتماعی خود استفاده می‌کنیم، و اگر بتوانیم اول با شما تماس می‌گیریم. حق نشر داستانتان برای خودتان می‌ماند.",
        "ما می‌توانیم هر ارسالی را به تشخیص خود رد یا حذف کنیم، و محتوایی را که قانون الزام می‌کند باید گزارش دهیم.",
      ],
    },
    {
      heading: "پس‌گرفتن",
      paragraphs: ["از پیوند خصوصی‌تان استفاده کنید یا به ما ایمیل بزنید. مطالب استفاده‌نشده حذف می‌شوند. مطالبی که قبلاً در یک تولید استفاده شده‌اند سابقهٔ رضایتشان را نگه می‌دارند؛ استفاده‌های تازه متوقف می‌شود و نسخه‌های منتشرشده قابل بازگرداندن نیست."],
    },
    {
      heading: "مسئولیت و قانون",
      paragraphs: ["این سایت همان‌طور که هست ارائه می‌شود. ما تیم کوچکی هستیم و نمی‌توانیم قول بدهیم به هر داستانی پاسخ دهیم. قانون سوئد حاکم است و دادگاه‌های سوئد صلاحیت دارند، بدون آن‌که حقوق مصرف‌کننده‌ای که در محل زندگی‌تان دارید محدود شود."],
    },
  ],
};

export const LEGAL: Record<"privacy" | "terms", Record<Locale, LegalDoc>> = {
  privacy: { en: privacyEn, fa: privacyFa },
  terms: { en: termsEn, fa: termsFa },
};
